import { Link } from "react-router-dom";
import { ArrowUpRight, Phone } from "lucide-react";
import { useSite } from "@/lib/site";
import { Reveal } from "./common";

export const Ticker = () => {
  const { content } = useSite();
  const items = (content.ticker || "").split(",").map((s) => s.trim()).filter(Boolean);
  const row = [...new Set(items)];
  return (
    <div className="bg-blue-600 text-white overflow-hidden py-5 border-y border-blue-700" data-testid="home-ticker">
      <div className="flex w-max marquee">
        {["a", "b", "c", "d"].map((copy) => (
          <div key={copy} className="flex shrink-0" aria-hidden={copy !== "a"}>
            {row.map((t) => (
              <span key={t} className="flex items-center font-display font-bold text-xl sm:text-2xl tracking-tight uppercase px-6">
                {t}<span className="ml-12 text-sky-300">✳</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export const CtaBand = () => {
  const { content } = useSite();
  const phone = content.phone || "+1 323 786 1615";
  return (
    <section className="px-3 sm:px-5 py-16 sm:py-24">
      <Reveal className="max-w-7xl mx-auto relative overflow-hidden rounded-[2rem] bg-blue-600 text-white px-6 sm:px-14 py-14 sm:py-20 grain" >
        <div className="absolute -right-24 -top-24 w-80 h-80 rounded-full bg-sky-400/40 blur-3xl" />
        <div className="relative max-w-3xl" data-testid="cta-band">
          <p className="overline !text-sky-200 mb-4">Ready for the next move?</p>
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">{content.cta_title}</h2>
          <p className="mt-5 text-lg text-blue-100">{content.cta_body}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="cta-contact-btn" className="inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 px-6 py-3.5 font-semibold hover:bg-slate-900 hover:text-white transition-colors">
              Start a conversation <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} data-testid="cta-phone-btn" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3.5 font-semibold hover:bg-white/10 transition-colors">
              <Phone className="w-4 h-4" /> Call {phone}
            </a>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export const STEPS = [
  ["Discover", "We understand your current challenges, goals and audience."],
  ["Design", "We agree on deliverables, pricing and a timeline before work begins."],
  ["Build", "We develop, launch and improve based on the agreed scope."],
  ["Evolve", "We review results and explore the next meaningful improvement."],
];

export const Approach = () => (
  <section className="relative bg-[#090D16] text-white overflow-hidden grain" data-testid="home-approach">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 relative">
      <Reveal className="grid lg:grid-cols-12 gap-8 items-end">
        <div className="lg:col-span-7">
          <p className="overline !text-sky-400 mb-4">Our approach</p>
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">Clear steps. <span className="text-slate-500">Stronger work.</span></h2>
        </div>
        <p className="lg:col-span-5 text-slate-400 text-lg">Every engagement starts with listening. Then we define a sensible scope, deliver the work and review what comes next.</p>
      </Reveal>
      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 border-t border-white/10">
        {STEPS.map(([t, d], i) => (
          <Reveal key={t} delay={i * 0.08} className="group border-b lg:border-b-0 sm:border-r border-white/10 last:border-r-0 p-6 sm:p-8 hover:bg-white/[0.03] transition-colors">
            <p className="font-mono text-sm text-sky-400">0{i + 1}</p>
            <p className="mt-10 font-display text-2xl font-bold" data-testid={`approach-step-${t.toLowerCase()}`}>{t}</p>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">{d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
